import { jsx, jsxs } from "react/jsx-runtime";
import { useId, useRef, forwardRef, Fragment } from "react";
import { RemoveScroll } from "react-remove-scroll";
import { useFallbackId } from "../../../hooks/useFallbackId.mjs";
import { Bleed } from "../../Bleed/Bleed.mjs";
import { Box } from "../../Box/Box.mjs";
import { ButtonIcon } from "../../ButtonIcon/ButtonIcon.mjs";
import { Heading } from "../../Heading/Heading.mjs";
import { gutters } from "../../PageBlock/PageBlock.mjs";
import { Stack } from "../../Stack/Stack.mjs";
import { buildDataAttributes } from "../buildDataAttributes.mjs";
import { normalizeKey } from "../normalizeKey.mjs";
import { pointerEventsAll, maxSize, closeIconOffset, headingRoot } from "./Modal.css.mjs";
import { IconClear } from "../../icons/IconClear/IconClear.mjs";
const modalPadding = { mobile: "gutter", tablet: "large" };
const ModalContentHeader = forwardRef(
  ({ title, headingLevel, description, descriptionId, center }, ref) => /* @__PURE__ */ jsxs(
    Stack,
    {
      space: headingLevel === "2" ? { mobile: "small", tablet: "medium" } : "small",
      children: [
        /* @__PURE__ */ jsx(Heading, { level: headingLevel, align: center ? "center" : void 0, children: /* @__PURE__ */ jsx(
          Box,
          {
            ref,
            tabIndex: -1,
            component: "span",
            position: "relative",
            outline: "focus",
            borderRadius: "small",
            className: headingRoot,
            children: title
          }
        ) }),
        description ? /* @__PURE__ */ jsx(Box, { id: descriptionId, children: description }) : null
      ]
    }
  )
);
const ModalContent = ({
  id,
  children,
  description,
  onClose,
  width,
  closeLabel = "Close",
  illustration,
  title,
  headingRef: headingRefProp,
  modalRef: modalRefProp,
  scrollLock = true,
  position,
  headingLevel,
  data,
  ...restProps
}) => {
  const resolvedId = useFallbackId(id);
  const descriptionId = useId();
  const defaultModalRef = useRef(null);
  const modalRef = modalRefProp || defaultModalRef;
  const defaultHeadingRef = useRef(null);
  const headingRef = headingRefProp || defaultHeadingRef;
  const handleEscape = (event) => {
    const targetKey = normalizeKey(event);
    if (targetKey === "Escape") {
      event.stopPropagation();
      onClose();
    }
  };
  const justifyContent = { left: "flexStart", center: "center", right: "flexEnd" }[position];
  const modalRadius = position === "center" ? "xlarge" : void 0;
  return /* @__PURE__ */ jsx(
    Box,
    {
      role: "dialog",
      "aria-label": title,
      "aria-describedby": description ? descriptionId : void 0,
      "aria-modal": "true",
      id: resolvedId,
      onKeyDown: handleEscape,
      position: "relative",
      width: "full",
      height: "full",
      display: "flex",
      alignItems: "center",
      textAlign: "left",
      justifyContent,
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          ref: modalRef,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent,
          height: position === "right" || position === "left" ? "full" : void 0,
          overflow: position !== "center" ? "hidden" : void 0,
          boxShadow: "large",
          borderRadius: modalRadius,
          width: width !== "content" ? "full" : void 0,
          maxWidth: width !== "content" ? width : void 0,
          children: [
            /* @__PURE__ */ jsx(
              RemoveScroll,
              {
                noRelative: true,
                forwardProps: true,
                enabled: scrollLock,
                children: /* @__PURE__ */ jsxs(
                  Box,
                  {
                    display: "flex",
                    gap: "large",
                    flexDirection: "column",
                    background: "surface",
                    borderRadius: modalRadius,
                    overflow: "auto",
                    position: "relative",
                    width: width !== "content" ? "full" : void 0,
                    height: position === "right" || position === "left" ? "full" : void 0,
                    paddingY: modalPadding,
                    paddingX: position !== "center" ? gutters : modalPadding,
                    className: [
                      pointerEventsAll,
                      position === "center" && maxSize[position]
                    ],
                    ...buildDataAttributes({ data, validateRestProps: restProps }),
                    children: [
                      illustration ? /* @__PURE__ */ jsxs(Stack, { space: "medium", align: "center", children: [
                        /* @__PURE__ */ jsx(Box, { paddingX: "gutter", children: illustration }),
                        /* @__PURE__ */ jsx(
                          ModalContentHeader,
                          {
                            title,
                            headingLevel,
                            description,
                            descriptionId,
                            center: Boolean(illustration),
                            ref: headingRef
                          }
                        )
                      ] }) : /* @__PURE__ */ jsxs(Box, { display: "flex", children: [
                        /* @__PURE__ */ jsx(Box, { width: "full", minWidth: 0, children: /* @__PURE__ */ jsx(
                          ModalContentHeader,
                          {
                            title,
                            headingLevel,
                            description,
                            descriptionId,
                            center: Boolean(illustration),
                            ref: headingRef
                          }
                        ) }),
                        /* @__PURE__ */ jsx(Box, { width: "touchable", flexShrink: 0, flexGrow: 0 })
                      ] }),
                      /* @__PURE__ */ jsx(Fragment, { children })
                    ]
                  }
                )
              }
            ),
            /* @__PURE__ */ jsx(
              Box,
              {
                position: "absolute",
                zIndex: "sticky",
                top: 0,
                pointerEvents: "none",
                width: "full",
                display: "flex",
                justifyContent: "flexEnd",
                paddingTop: modalPadding,
                paddingRight: position !== "center" ? gutters : modalPadding,
                className: position === "center" && maxSize[position],
                children: /* @__PURE__ */ jsx(Bleed, { space: "xsmall", children: /* @__PURE__ */ jsx(
                  Box,
                  {
                    position: "relative",
                    background: "surface",
                    borderRadius: "full",
                    padding: "xsmall",
                    className: [closeIconOffset, pointerEventsAll],
                    children: /* @__PURE__ */ jsx(
                      ButtonIcon,
                      {
                        label: closeLabel,
                        icon: /* @__PURE__ */ jsx(IconClear, { tone: "secondary" }),
                        variant: "transparent",
                        size: "large",
                        onClick: onClose
                      }
                    )
                  }
                ) })
              }
            )
          ]
        }
      )
    }
  );
};
export {
  ModalContent
};
